import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, limit, doc, updateDoc, getDoc, arrayUnion } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import { Feedback, FeedbackType, User } from '../types';

// Re-export FeedbackType for components
export type { FeedbackType };
import { awardXP, XP_ACTIONS } from './gamificationService';

const FEEDBACK_MAX_LENGTH = 500;
const RATE_LIMIT_MINUTES = 5; // Rate limit: max 1 feedback per 5 minutes per user

/**
 * Get device/platform information for metadata
 */
const getDeviceMetadata = () => {
  const userAgent = navigator.userAgent;
  let platform = 'unknown';
  
  if (/Win/.test(userAgent)) platform = 'Windows';
  else if (/Mac/.test(userAgent)) platform = 'macOS';
  else if (/Linux/.test(userAgent)) platform = 'Linux';
  else if (/Android/.test(userAgent)) platform = 'Android';
  else if (/iPhone|iPad|iPod/.test(userAgent)) platform = 'iOS';
  
  return {
    userAgent,
    platform,
    appVersion: import.meta.env.VITE_APP_VERSION || '3.0.0',
  };
};

/**
 * Check rate limiting - prevent spam
 * Note: Firestore timestamp comparison is complex, so we fetch recent feedback and check client-side
 */
const checkRateLimit = async (userId: string): Promise<boolean> => {
  try {
    // Get recent feedback and check client-side
    const q = query(
      collection(db, "feedback"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(10) // Check last 10 feedback to find recent ones
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) return true; // No feedback yet, allow
    
    // Check if any feedback was created in the last RATE_LIMIT_MINUTES
    const fiveMinutesAgo = Date.now() - RATE_LIMIT_MINUTES * 60 * 1000;
    const hasRecentFeedback = snapshot.docs.some(doc => {
      const data = doc.data();
      const createdAt = data.createdAt?.toMillis?.() || (data.createdAt?.seconds ? data.createdAt.seconds * 1000 : 0);
      return createdAt > fiveMinutesAgo;
    });
    
    return !hasRecentFeedback; // Returns true if no recent feedback (allowed)
  } catch (error) {
    console.error("Rate limit check error:", error);
    // If orderBy fails (no index), allow submission (fail open)
    // The user should create an index in Firestore Console if this happens
    return true;
  }
};

/**
 * Upload screenshot to Firebase Storage
 */
const uploadScreenshot = async (file: File, userId: string): Promise<string> => {
  try {
    const timestamp = Date.now();
    const filename = `feedback/${userId}/${timestamp}_${file.name}`;
    const storageRef = ref(storage, filename);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    console.error("Screenshot upload error:", error);
    throw new Error("Failed to upload screenshot");
  }
};

/**
 * Submit feedback to Firestore and award XP if eligible
 */
export const submitFeedback = async (
  userId: string,
  userName: string,
  userEmail: string,
  type: FeedbackType,
  rating: number,
  message: string,
  screenshotFile?: File,
  additionalMetadata?: {
    view?: string;
    plan?: string;
    language?: string;
  }
): Promise<{ feedbackId: string; xpAwarded: boolean }> => {
  // Validate message length
  if (message.length > FEEDBACK_MAX_LENGTH) {
    throw new Error(`Message too long. Maximum ${FEEDBACK_MAX_LENGTH} characters allowed.`);
  }

  // Validate rating
  if (rating < 1 || rating > 5) {
    throw new Error("Rating must be between 1 and 5");
  }

  // Validate feedback has content (rating is required, but also check if message is meaningful)
  const hasValidContent = rating > 0 && message.trim().length > 0;
  if (!hasValidContent) {
    throw new Error("Please provide a rating and message for your feedback");
  }

  // Check rate limiting
  const canSubmit = await checkRateLimit(userId);
  if (!canSubmit) {
    throw new Error(`Please wait ${RATE_LIMIT_MINUTES} minutes before submitting another feedback.`);
  }

  // Upload screenshot if provided
  let screenshotUrl: string | undefined;
  if (screenshotFile) {
    screenshotUrl = await uploadScreenshot(screenshotFile, userId);
  }

  // Get device metadata
  const deviceMetadata = getDeviceMetadata();
  const appVersion = deviceMetadata.appVersion;

  // ANTI-ABUSE: Check if user has already received XP for feedback in this app version
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  let xpAwarded = false;
  const feedbackXPVersions = (userSnap.data() as User)?.feedbackXPVersions || [];
  const hasReceivedXPForVersion = feedbackXPVersions.includes(appVersion);

  // Create feedback document (without xpAwarded initially)
  const feedbackData: Omit<Feedback, 'id'> = {
    userId,
    userName,
    userEmail,
    type,
    rating,
    message: message.trim(),
    screenshotUrl,
    metadata: {
      ...deviceMetadata,
      ...additionalMetadata,
    },
    status: 'new',
    xpAwarded: false, // Will be updated after XP award
    createdAt: serverTimestamp(),
  };

  // Save feedback FIRST (as per requirement: feedback must be saved before awarding XP)
  const feedbackRef = await addDoc(collection(db, "feedback"), feedbackData);

  // Award XP only if:
  // 1. User hasn't received XP for this app version yet
  // 2. Feedback is valid (has rating and message)
  if (!hasReceivedXPForVersion && hasValidContent) {
    try {
      // Award 500 XP
      await awardXP(userId, XP_ACTIONS.FEEDBACK, "Feedback Submission");

      // Update feedback document to mark XP as awarded
      await updateDoc(feedbackRef, {
        xpAwarded: true
      });

      // Update user document to track this app version
      await updateDoc(userRef, {
        feedbackXPVersions: arrayUnion(appVersion)
      });

      xpAwarded = true;
    } catch (error) {
      console.error("Error awarding XP for feedback:", error);
      // Don't throw - feedback was saved successfully, XP failure is non-critical
      // The feedback will remain with xpAwarded: false
    }
  }

  return { feedbackId: feedbackRef.id, xpAwarded };
};

/**
 * Get user's feedback history (optional, for future use)
 */
export const getUserFeedback = async (userId: string) => {
  try {
    const q = query(
      collection(db, "feedback"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(10)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Feedback));
  } catch (error) {
    console.error("Error fetching user feedback:", error);
    return [];
  }
};

